<template>
  <b-card class="referral-card" no-body>
    <template v-if="contact">
      <b-card-body class="referral-card-body d-flex flex-row justify-content-top align-items-top" v-b-modal="referralID">
        <img class="flex-shrink-0 referral-contact-picture mr-2" :src="avatar" height="40" width="40" v-b-modal="referralID"/>
        <div class="referral-details d-flex flex-column">
          <div class="text-black"><strong>{{ contact.fullName }}</strong></div>
          <div class="text-secondary-a5">Referred with <strong>{{ job.title }}</strong></div>
          <div class="status text-black">
            <strong>{{ referral.created | formatDate('dddd, Do MMMM YYYY') }}</strong>
          </div>
          <div class="status text-black" v-if="status === 'IN_PROGRESS'">
            <strong>Application Stage</strong>
          </div>
          <div class="status text-black" v-if="status === 'HIRED'">
            <strong>Hired</strong>
          </div>
        </div>
      </b-card-body>
      <contact-modal v-if="contact" :id="referralID" :contact="contact"/>
    </template>
    <b-card-body class="referral-card-body d-flex flex-row justify-content-top align-items-top" v-else>
      <avatar class="mr-2" :fullName="referral.fullName" :size="40"/>
      <div class="referral-details d-flex flex-column">
        <div class="text-black">
          <template v-if="referral.fullName">
            <strong>{{ referral.fullName }}</strong>
            <span class="text-muted" v-if="referral.emailAddress">({{ referral.emailAddress }})</span>
          </template>
          <strong v-else>Contact</strong>
        </div>
        <div class="text-secondary-a5">
          Referred for <strong>{{ job.title }}</strong>
        </div>
        <div class="status text-black" v-if="status === 'SUGGESTED' || status === 'ACCEPTED'">
          <strong>{{ referral.created | formatDate('dddd, Do MMMM YYYY') }}</strong>
        </div>
        <div class="status text-black" v-if="status === 'IN_PROGRESS'">
          <strong>Application Stage</strong>
        </div>
        <div class="status text-black" v-if="status === 'HIRED'">
          <strong>Hired</strong>
        </div>
      </div>
    </b-card-body>
    <b-card-footer class="referral-card-footer">
      <template v-if="status === 'ACCEPTED'">
        <h4>
          <router-link :to="{ name: 'JobShow', params: { jobID } }">View Job</router-link> -
          <a href="#" @click.prevent v-b-modal="`share-${referral.id}`">Share Again</a>
        </h4>
        <b-modal :id="`share-${referral.id}`" hide-header hide-footer lazy>
          <share v-if="referral.id && job && contact && referral.shareCode"
            :title="`Share this role with ${contact.fullName}`" :url="`${$root.origin}/r/${referral.shareCode}`"
            show-preview :preview-args="{ title: job.title }"
            @modal-close="() => $bvModal.hide(`share-${referral.id}`)" @track="trackShareEvent">
              <template v-slot:text>
                <p>Click one of the buttons below to share with {{contact.fullName}} &amp; we'll let you know when they apply.</p>
              </template>
          </share>
        </b-modal>
      </template>
      <p v-else-if="referral.emailAddress" class="text-muted m-0">Email sent</p>
    </b-card-footer>
  </b-card>
</template>

<script>
import ContactModal from '@/modals/contact-profile';
import ReferQuestions from '@/components/job-referrals/refer-questions';
import Avatar from '@/components/users/Avatar';
import Share from '@/components/Share';
import ReferralsEmployeeAnswerQuestionsMutation from '@/graphql/mutations/ReferralsEmployeeAnswerQuestions.gql';

export default {
  name: 'JobReferralLaneItem',
  props: {
    referral: {
      type: Object,
    },
  },
  components: {
    Avatar,
    ContactModal,
    ReferQuestions,
    Share,
  },
  data: () => ({
    referAnswers: {},
  }),
  computed: {
    referralID() {
      const { id } = this.referral;
      return id || null;
    },
    contact() {
      const { contact } = this.referral;
      return contact && contact.id ? contact : null;
    },
    contactID() {
      const { contact } = this.referral;
      return contact && contact.id ? contact.id : null;
    },
    job() {
      const { job } = this.referral;
      return job && job.id ? job : null;
    },
    jobID() {
      const { job } = this.referral;
      return job && job.id ? job.id : null;
    },
    avatar() {
      if (this.contact && this.contact.avatar) {
        return `/static/images/avatars/main${this.contact.avatar}.png`;
      } else {
        return '/static/images/avatars/main1.png';
      }
    },
    status() {
      const { status } = this.referral;
      return status || null;
    },
    inProgressState() {
      if (this.referralID && this.status === 'IN_PROGRESS') {
        const { howWellRating } = this.referral;
        if (howWellRating === null || howWellRating === undefined) {
          return 'AWAITING_ANSWERS';
        }

        // More checks for more In-Progress state here
        // @TODO Compute & return server-side?
      }

      return null;
    },
  },
  methods: {
    async sendReferAnswers() {
      const { id, contact: { id: contactID }, job: { id: jobID }, status } = this.referral;
      if (!id || !contactID || !jobID || !status) {
        throw new TypeError('Missing id/contactID/jobID/status from referral');
      }

      await this.$apollo.mutate({
        mutation: ReferralsEmployeeAnswerQuestionsMutation,
        variables: {
          input: {
            contactID,
            jobID,
            ...this.referAnswers,
            status,
          },
        },
      });

      this.$root.$emit('bv::hide::modal', `awaiting-answers-${id}`);
    },
    trackShareEvent({ channel }) {
      const { shareCode } = this.referral;
      Promise.resolve(this.$root.captureLog({
        eventName: 'REFERRAL_SHARED',
        job: { share: shareCode },
        user: { share: shareCode },
        referral: { share: shareCode },
        data: channel ? [ { key: 'channel', value: channel } ] : null,
      }));
    },
  },
};
</script>

<style lang="scss">
@import './../../scss/variables';

.referral-card {

  .referral-card-body {
    .referral-details {
      font-size: $small-font-size;
      min-width: 15rem;
      text-align: left;
    }

    img {
      border-radius: 50%;
    }
  }
  .referral-card-footer {
    background-color: white;
    text-align: center;

    h4 {
      margin-bottom: 0px;
      font-size: $small-font-size;
      a {
        font-weight: 500;
      }
    }
  }
  .circle, .match-label, .btn, .dropdown, .workplace {
    margin: 0 auto;
  }
  .workplaces {
    height: 10rem;
    border-top: 1px solid rgba(0, 0, 0, 0.125);
  }

  .btn, .dropdown {
    max-width: 13rem;
    min-width: 10rem;
    width: 100%;
    height: 3rem;
    padding-bottom: 0.3rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/job-referrals/lane-item.vue